import { motion } from "framer-motion";
import { UploadCloud, BadgeCheck, BellRing } from "lucide-react";

const STEPS = [
  {
    icon: UploadCloud,
    n: "Step 01",
    title: "Post what you bought",
    body: "Drop the ad link, what you paid, and what you actually got. Attach your payment and product screenshots.",
  },
  {
    icon: BadgeCheck,
    n: "Step 02",
    title: "It gets verified",
    body: "Reviews with both proofs get the verified stamp. Everything is a factual account of a real purchase — checked before it counts.",
  },
  {
    icon: BellRing,
    n: "Step 03",
    title: "The next buyer is warned",
    body: "Someone searches that seller before paying, finds your receipt, and keeps their money. Then they add theirs. It compounds.",
  },
];

export const HowItWorks = () => (
  <section data-testid="how-it-works-section" className="py-24 sm:py-32">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <div className="flex items-center gap-4">
          <span className="font-display text-4xl sm:text-5xl font-bold outline-word leading-none select-none" aria-hidden="true">05</span>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-trust">How it works</p>
        </div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Simple enough that busy people actually use it.
        </h2>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            data-testid={`how-step-${i}`}
            className="relative rounded-2xl bg-white border border-black/5 p-8 shadow-[0_10px_36px_-14px_rgba(17,24,39,0.12)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.12 }}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-trust-soft text-trust">
                <s.icon size={20} />
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">{s.n}</span>
            </div>
            <h3 className="mt-6 font-display text-xl font-semibold tracking-tight">{s.title}</h3>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed">{s.body}</p>
            {i < STEPS.length - 1 && (
              <span className="hidden md:block absolute top-1/2 -right-5 w-4 h-px bg-gray-300" aria-hidden="true" />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
